import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, Validators } from '@angular/forms';

import { SharedModule } from 'src/app/shared/shared.module';
import { RadioButtonModule } from 'primeng/radiobutton';

import { AnimalModel } from 'src/app/modules/animals/models/animal.model';
import { AnimalOriginModel } from 'src/app/modules/animals/models/animal-origin.model';

import { AnimalRepository } from 'src/app/modules/animals/states/animal.repository';
import { ToastService } from 'src/app/shared/services/toast.service';
import { WaitingDialogService } from 'src/app/shared/services/waiting-dialog.service';

import { forkJoin, from, of } from 'rxjs';
import { concatMap, map, switchMap, take, toArray } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { AnimalAddService, OriginContactEntry } from 'src/app/modules/animals/services/animal-add.service';
import { FoundReportPreviewBuilderService } from 'src/app/modules/animals/services/found-report-preview-builder.service';
import { FoundReportPdfRepository } from 'src/app/modules/animals/states/pdf.repository';
import { AnimalOriginReasonForTransfer } from 'src/app/modules/animals/models/animal-origin-reason-for-transfer.enum';
import { CareCondition, NutritionCondition, TemperamentCondition } from 'src/app/modules/animals/models/body-condition-score.model';
import { StepsModule } from 'primeng/steps';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { AnimalCoatTypeModel } from 'src/app/modules/animals/models/animal-coat-type.model';
import { AnimalService } from 'src/app/modules/animals/services/animal.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { ContactModel } from 'src/app/modules/contact/models/contact.model';
import { SendFoundReportBundleDialogComponent, BundleSendItem } from 'src/app/shared/components/send-found-report-bundle-dialog/send-found-report-bundle-dialog.component';

export type DuplicateAnimalPayload = {
  animal: Partial<AnimalModel>;
  origin: Partial<AnimalOriginModel> | null;
};

type AnimalDraft = {
  name: string | null;
  nameOld: string | null;
  category: string | null;
  type: string | null;
  breed: string | null;
  sex: 'male' | 'female' | null;

  coatType: string[] | null;
  coatColor: string | null;
  badge: string | null;
  specialNotice: string | null;

  birthApprox: boolean | null;
  birthdayType: boolean | null;
  birthday: string | null;
  birthYear: string | null;
  birthPlace: string | null;

  weight: number | null;

  oldBookNumberList: (string | null)[];
  oldBookNumber: string | null;
  transponderNumber: (string | null)[];
  lifeNumber: string | null;
  petPassportNumber: string | null;
  equinePassportNumber: string | null;
  ringNumber: string | null;
  earTag: string | null;
  tattoo: string | null;
  tattooLocation: string | null;

  motherId: number | null;
  mother?: AnimalModel | null;
};

type OriginDraft = {
  dateOfReceipt: string | null;
  location: string | null;
  specialInformation: string | null;
  otherComments: string | null;
};

type BcsControls = {
  careCtrl: FormControl<CareCondition[] | null>;
  nutritionCtrl: FormControl<NutritionCondition | null>;
  temperamentCtrl: FormControl<TemperamentCondition[] | null>;
};

type DuplicateItem = {
  animal: AnimalDraft;
  origin: OriginDraft;
  bcs?: BcsControls;
};

export type FoundProcessPhase = 'edit' | 'pdf' | 'signatures';

/** Geteilte Fundtier-Daten, die der Wizard an alle Duplikate weiterreicht. */
export interface FoundProcessSharedInput {
  shelterId: number;
  foundStatus: string | null;
  // Auffinddatum + Uhrzeit (geteilt für alle Fundtiere desselben Fundes) – mit Zeitanteil!
  foundDateTime: Date | string | null;
  pickupUserName: string | null;
  pickUpDateTime: Date | string | null;
  consentToShareWithOwner: boolean;
  competentAuthorityId: number | string | null;
  signatureLocation: string | null;
  signatureDate: Date | string | null;
  contacts: OriginContactEntry[];
  /** Kontakte der zuständigen Behörde (Empfänger im PDF-Versand). */
  authorityContacts: ContactModel[];
  /** Origin-Id des bereits existierenden Originals (nur animal-details; im Wizard null). */
  originalOriginId?: number | null;
}

@Component({
  selector: 'app-duplicate-animals',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, RadioButtonModule, StepsModule, MultiSelectModule, ConfirmDialogModule, SendFoundReportBundleDialogComponent],
  templateUrl: './duplicate-animals.component.html',
  styleUrls: ['./duplicate-animals.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DuplicateAnimalsComponent implements OnInit {
  @Input() animal!: AnimalModel;
  @Input() origin?: AnimalOriginModel | null;

  @Input() excludedAnimalFields: string[] = [
    // technisch / serverseitig
    'id',
    'createdAt',
    'updatedAt',

    // Nummern, die wir explizit NICHT übernehmen
    'registrationNumber',
    'chipNumber',
    'oldBookNumberList',
    'oldBookNumber',
    'transponderNumber',
    'lifeNumber',
    'petPassportNumber',
    'equinePassportNumber',
    'ringNumber',
    'earTag',
    'tattoo',
    'tattooLocation',

    // Felder, die NICHT vom Parent-Tier übernommen werden dürfen
    'name',
    'bookNumber',
    'displayName',
    'origins',
    'previewImageId',
    'previousBookNumbers',
    'profileAttachmentUrl',
    'whereabouts',
  ];
  @Input() excludedOriginFields: string[] = ['id', 'createdAt', 'updatedAt'];

  @Input() initialCount: number | null = null;

  /** Fundtierprozess-Modus: aktiviert Phasen (edit → pdf → signatures) + BCS pro Tier. */
  @Input() foundProcessMode = false;
  /** Geteilte Fundtier-Daten (Finder, Behörde, Pickup, Signatur-Defaults) für alle Duplikate. */
  @Input() foundShared: FoundProcessSharedInput | null = null;
  /**
   * Ob das Original-Tier als erstes Element (für PDF-Vorschau + Speichern) mitgeführt wird.
   * Wizard-Flow: true (Original ist neu). animal-details: false (Original existiert bereits inkl. PDF).
   */
  @Input() foundIncludeOriginal = true;

  @Output() closed = new EventEmitter<void>();
  visible = true;

  items: DuplicateItem[] = [];
  weightInputInvalid: boolean[] = [];

  stepsItems: MenuItem[] = [];
  activeStepIndex = 0;

  coatTypeOptions: AnimalCoatTypeModel[] = [];

  // Vom Original geerbte Mutter (einmal nachgeladen für die Anzeige)
  private inheritedMother: AnimalModel | null = null;

  // ----- Fundtierprozess-Modus -----
  phase: FoundProcessPhase = 'edit';
  // Das Original (aus dem Wizard) wird NICHT in `items` gelistet/editiert,
  // aber für PDF-Vorschau und Speichern mitgeführt.
  originalItem: DuplicateItem | null = null;
  // Reihenfolge der PDF-Vorschau (Original zuerst, dann Duplikate) – index-gleich zu previewBlobs.
  previewItems: DuplicateItem[] = [];
  previewBlobs: (Blob | null)[] = [];
  isGeneratingPreviews = false;

  // PDF-Versand (Bundle) nach dem Speichern
  bundleSendVisible = false;
  bundleItems: BundleSendItem[] = [];
  sharedSignature: {
    finderSignature: string | null;
    employeeSignature: string | null;
    signatureLocation: string | null;
    signatureDate: Date | string | null;
  } = { finderSignature: null, employeeSignature: null, signatureLocation: null, signatureDate: null };

  private _animalRepository = inject(AnimalRepository);
  private _animalAddService = inject(AnimalAddService);
  private _toast = inject(ToastService);
  private _translateService = inject(TranslocoService);
  private _waitingDialogService = inject(WaitingDialogService);
  private _animalService = inject(AnimalService);
  private _confirm = inject(ConfirmationService);
  private _cdr = inject(ChangeDetectorRef);
  private _previewBuilder = inject(FoundReportPreviewBuilderService);
  private _pdfRepo = inject(FoundReportPdfRepository);

  ngOnInit(): void {
    this.coatTypeOptions = this._animalService.getCoatTypes();

    if (this.foundProcessMode) {
      // Original nur mitführen, wenn es noch nicht existiert (Wizard). Aus animal-details
      // (Original ist bereits gespeichert + hat PDF) wird es NICHT mitgeführt.
      this.originalItem = this.foundIncludeOriginal ? this.buildPrefilledItem(true) : null;
      // Editierbare Liste startet mit einem leeren Duplikat.
      this.items = [this.buildPrefilledItem(false)];
      this.weightInputInvalid = [false];
      if (this.foundShared) {
        this.sharedSignature.signatureLocation = this.foundShared.signatureLocation ?? null;
        this.sharedSignature.signatureDate = this.foundShared.signatureDate ?? null;
      }
    } else {
      const n = this.initialCount && this.initialCount > 0 ? this.initialCount : 1;

      this.items = [];
      this.weightInputInvalid = new Array(n).fill(false);

      for (let i = 0; i < n; i++) {
        this.items.push(this.buildPrefilledItem());
      }
    }

    this.rebuildSteps();
    this.activeStepIndex = 0;

    this.loadInheritedMother();
  }

  /** Lädt – sofern das Original eine Mutter hat – das Mutter-Tier einmalig für die Anzeige nach. */
  private loadInheritedMother(): void {
    const motherId = this.animal?.motherId;
    if (!motherId) {
      return;
    }
    this._animalRepository.getById(motherId).pipe(take(1)).subscribe(m => {
      if (!m) {
        return;
      }
      this.inheritedMother = m;
      this.items.forEach(it => {
        if (it.animal.motherId === motherId && !it.animal.mother) {
          it.animal.mother = m;
        }
      });
      if (this.originalItem && this.originalItem.animal.motherId === motherId && !this.originalItem.animal.mother) {
        this.originalItem.animal.mother = m;
      }
      this._cdr.markForCheck();
    });
  }

  onMotherSelected(index: number, animal: AnimalModel): void {
    const item = this.items[index];
    if (!item) {
      return;
    }
    item.animal.motherId = animal.id ?? null;
    item.animal.mother = animal;
    this._cdr.markForCheck();
  }

  onCancel(): void {
    this.closed.emit();
  }

  onVisibleChange(v: boolean): void {
    // Wird von PrimeNG gefeuert, wenn X, ESC oder Overlay-Klick passiert
    if (!v) {
      this.onCancel();
    }
  }

  onAddDuplicate(): void {
    const newItem = this.buildPrefilledItem();
    this.items.push(newItem);

    const idx = this.items.length - 1;
    this.weightInputInvalid[idx] = false;

    this.rebuildSteps();
  this.activeStepIndex = idx
  }


  onNameChanged(index: number, value: string): void {
  this.items[index].animal.name = value;

  // Nur den einen Step aktualisieren:
  const item = this.items[index];
  const newLabel = this.buildStepLabel(item, index);

  if (this.stepsItems[index]) {
    this.stepsItems[index] = {
      ...this.stepsItems[index],
      label: newLabel
    };
    this.stepsItems = [...this.stepsItems];
  }
}

  private buildStepLabel(item: DuplicateItem, index: number): string {
    const name = item.animal.name?.trim();
    if (name) {
      return name;
    }

    // Fallback: "Duplikat 1 / 2 / 3 ..." o.ä.
    return this._translateService.translate(
      'animalDuplicate.duplicateLabel',
      { i: index + 1 }
    );
  }

  private rebuildSteps(): void {
  this.stepsItems = this.items.map((item, index) => ({
    label: this.buildStepLabel(item, index)
  }));
}

onStepChange(index: number): void {
  if (index < 0 || index >= this.items.length) return;
  this.activeStepIndex = index;
}

onPrevStep(): void {
  if (this.activeStepIndex > 0) {
    this.activeStepIndex--;
  }
}

onNextStep(): void {
  if (this.activeStepIndex < this.items.length - 1) {
    this.activeStepIndex++;
  }
}

onDeleteCurrentDuplicate(): void {
  if (this.items.length === 0) return;

  const idx = this.activeStepIndex;

  this._confirm.confirm({
    header: this._translateService.translate('animalDuplicate.confirmDelete.header'),
    message: this._translateService.translate('animalDuplicate.confirmDelete.message'),
    acceptLabel: this._translateService.translate('yes'),
    rejectLabel: this._translateService.translate('no'),
    icon: 'pi pi-exclamation-triangle',
    accept: () => {
      this._confirm.close();

      try {
        this.items.splice(idx, 1);
        this.weightInputInvalid.splice(idx, 1);

        if (this.activeStepIndex >= this.items.length) {
          this.activeStepIndex = Math.max(0, this.items.length - 1);
        }

        this.rebuildSteps();
      } catch (e) {
        console.error('Delete duplicate failed:', e);
      }
    },
    reject: () => this._confirm.close(),
  });
}

  onSubmit(): void {
    if (this.foundProcessMode) {
      this.submitFoundProcess();
      return;
    }

    this._waitingDialogService.showWaitingDialog(true);

    from(this.items)
      .pipe(
        concatMap((it, index) => {
          const animalPayload = this.buildAnimalPayload(it.animal);
          const originBase = {
            ...this.buildOriginPayload(it.origin),
            careCondition: it.bcs?.careCtrl.value ?? [],
            bcsNutrition: it.bcs?.nutritionCtrl.value ?? null,
            temperament: it.bcs?.temperamentCtrl.value ?? [],
          };


          return this._animalRepository.create(animalPayload).pipe(
            take(1),
            concatMap((newAnimal: AnimalModel) => {
              if (newAnimal.id === undefined || newAnimal.id === null) {
                console.error('Kein newAnimal.id für Duplikat', index);
                return of(null);
              }

              const originPayload: AnimalOriginModel | any = {
                ...originBase,
                animal: { id: newAnimal.id },
              };

              if (!originPayload.animalShelter) {
                originPayload.animalShelter = this.animal?.animalShelter ?? null;
              }

              this._animalAddService.createOrigin(originPayload, newAnimal.id);

              return of(null);
            })
          );
        }),
        toArray(),
        take(1)
      )
      .subscribe({
        next: () => {
          this._waitingDialogService.showWaitingDialog(false);
          this._toast.showSuccess(
            this._translateService.translate('animalAdd.toast.saveSuccess')
          );
          this.closed.emit();
        },
        error: (error) => {
          console.error('Error creating duplicate animals:', error);
          this._waitingDialogService.showWaitingDialog(false);
          this._toast.showError(this._translateService.translate('animalAdd.toast.saveError'));
        },
      });
  }

  // ---------- Fundtierprozess (Phasen) ----------
  get isFoundEditPhase(): boolean { return this.foundProcessMode && this.phase === 'edit'; }
  get isFoundPdfPhase(): boolean { return this.foundProcessMode && this.phase === 'pdf'; }
  get isFoundSignaturePhase(): boolean { return this.foundProcessMode && this.phase === 'signatures'; }

  onPhaseNext(): void {
    if (this.phase === 'edit') {
      if (!this.allItemsValidForPdf()) return;
      this.generateAllPreviews();
    } else if (this.phase === 'pdf') {
      this.phase = 'signatures';
      this._cdr.markForCheck();
    }
  }

  onPhaseBack(): void {
    if (this.phase === 'signatures') {
      this.phase = 'pdf';
    } else if (this.phase === 'pdf') {
      this.phase = 'edit';
    }
    this._cdr.markForCheck();
  }

  /** BCS unvollstaendig/ungueltig (Pflichtfelder nur im Fundtierprozess -> Warndreieck im Header). */
  isBcsInvalid(it: DuplicateItem): boolean {
    const bcs = it.bcs;
    if (!bcs) return false;
    return bcs.careCtrl.invalid || bcs.nutritionCtrl.invalid || bcs.temperamentCtrl.invalid;
  }

  isItemValidForPdf(it: DuplicateItem): boolean {
    return !!it.animal.breed?.trim()
      && !!it.animal.sex
      && !!this.foundShared?.foundDateTime
      && !!it.bcs
      && (it.bcs.careCtrl.value?.length ?? 0) > 0
      && it.bcs.nutritionCtrl.value != null
      && (it.bcs.temperamentCtrl.value?.length ?? 0) > 0;
  }

  allItemsValidForPdf(): boolean {
    return this.items.length > 0 && this.items.every(it => this.isItemValidForPdf(it));
  }

  isSignatureComplete(): boolean {
    const s = this.sharedSignature;
    return !!(s.finderSignature && s.employeeSignature && s.signatureLocation && s.signatureDate);
  }

  /** Original (falls vorhanden) + alle editierbaren Duplikate – für PDF-Vorschau und Speichern. */
  private collectFoundItems(): DuplicateItem[] {
    return this.originalItem ? [this.originalItem, ...this.items] : [...this.items];
  }

  /** Accordion-Header für die PDF-Vorschau: Original zuerst, dann Duplikat-Nummer. */
  previewLabel(idx: number): string {
    const it = this.previewItems[idx];
    const name = it?.animal?.name?.trim();
    if (name) return name;
    if (this.originalItem && idx === 0) {
      return this._translateService.translate('animalDuplicate.originalLabel');
    }
    const dupNumber = this.originalItem ? idx : idx + 1;
    return this._translateService.translate('animalDuplicate.duplicateLabel', { i: dupNumber });
  }

  /** Erzeugt pro Duplikat eine PDF-Vorschau (1 getPreview()-Call je Tier). */
  private generateAllPreviews(): void {
    if (!this.foundShared) return;
    const sh = this.foundShared;

    this.isGeneratingPreviews = true;
    this._cdr.markForCheck();

    const contactArgs = (sh.contacts ?? [])
      .filter(c => c.data?.isValid && !!c.data?.contact)
      .map(c => ({ contact: c.data.contact, type: c.meta.type }));

    const all = this.collectFoundItems();
    this.previewItems = all;
    const calls = all.map((it) => {
      const animalForPreview = this.buildAnimalPayload(it.animal) as AnimalModel;
      const formValueForPreview = {
        foundStatus: sh.foundStatus,
        originSpecialInformation: it.origin.specialInformation,
        originLocation: it.origin.location,
        foundDateTime: sh.foundDateTime ?? null,
        pickupUserName: sh.pickupUserName,
        pickUpDateTime: sh.pickUpDateTime,
        consentToShareWithOwner: sh.consentToShareWithOwner,
        careCondition: it.bcs?.careCtrl.value ?? [],
        temperament: it.bcs?.temperamentCtrl.value ?? [],
        bcsNutrition: it.bcs?.nutritionCtrl.value ?? null,
      };
      const payload = this._previewBuilder.build({
        animal: animalForPreview,
        formValue: formValueForPreview,
        shelterId: sh.shelterId,
        contacts: contactArgs,
      });
      return this._pdfRepo.getPreview(payload);
    });

    forkJoin(calls).pipe(take(1)).subscribe((blobs) => {
      this.previewBlobs = blobs;
      this.isGeneratingPreviews = false;
      this.phase = 'pdf';
      this._cdr.markForCheck();
    });
  }

  /** Legt im Fundtierprozess je Tier ein Tier + ein Fundtier-Origin an. */
  private submitFoundProcess(thenSend = false): void {
    if (!this.foundShared) return;
    const sh = this.foundShared;

    this._waitingDialogService.showWaitingDialog(true);

    from(this.collectFoundItems()).pipe(
      concatMap((it) => {
        const animalPayload = this.buildAnimalPayload(it.animal);
        return this._animalRepository.create(animalPayload).pipe(
          take(1),
          map((newAnimal: AnimalModel) => ({ it, newAnimal }))
        );
      }),
      toArray(),
      switchMap((pairs) => {
        const valid = pairs.filter(p => p.newAnimal?.id != null);
        const payloads = valid.map(p => this.buildFoundOriginPayload(p.it, p.newAnimal.id!));
        return this._animalAddService.createFoundOrigins(payloads, sh.contacts).pipe(
          map((origins) => ({ valid, origins }))
        );
      }),
      take(1)
    ).subscribe({
      next: ({ valid, origins }) => {
        this._waitingDialogService.showWaitingDialog(false);
        if (thenSend) {
          this.openBundleSend(valid, origins);
        } else {
          this._toast.showSuccess(this._translateService.translate('animalAdd.toast.saveSuccess'));
          this.closed.emit();
        }
      },
      error: (err) => {
        console.error('Error creating found duplicates:', err);
        this._waitingDialogService.showWaitingDialog(false);
        this._toast.showError(this._translateService.translate('animalAdd.toast.saveError'));
      },
    });
  }

  /** Öffnet nach dem Speichern den Bundle-Versand-Dialog für Original + Duplikate. */
  private openBundleSend(
    valid: { it: DuplicateItem; newAnimal: AnimalModel }[],
    origins: AnimalOriginModel[]
  ): void {
    const sh = this.foundShared!;
    const createdItems: BundleSendItem[] = origins
      .map((origin, i) => ({ origin, newAnimal: valid[i]?.newAnimal }))
      .filter(x => x.origin?.id != null && x.newAnimal != null)
      .map(x => ({ animalOriginId: x.origin.id, animal: x.newAnimal! }));

    const items: BundleSendItem[] = [];
    // animal-details: bereits existierendes Original ergänzen (im Wizard ist es Teil von createdItems)
    if (!this.foundIncludeOriginal && sh.originalOriginId && this.animal) {
      items.push({ animalOriginId: sh.originalOriginId, animal: this.animal });
    }
    items.push(...createdItems);

    this.bundleItems = items;
    this.bundleSendVisible = true;
    this._toast.showSuccess(this._translateService.translate('animalAdd.toast.saveSuccess'));
    this._cdr.markForCheck();
  }

  onBundleVisibleChange(visible: boolean): void {
    this.bundleSendVisible = visible;
    if (!visible) {
      this.closed.emit();
    }
  }

  private buildFoundOriginPayload(it: DuplicateItem, animalId: number): any {
    const sh = this.foundShared!;
    const payload: any = {
      animal: { id: animalId },
      type: AnimalOriginReasonForTransfer.FOUND_ANIMAL,
      foundDateTime: sh.foundDateTime ?? null,
      location: it.origin.location,
      specialInformation: it.origin.specialInformation,
      otherComments: it.origin.otherComments,
      foundStatus: sh.foundStatus ?? null,
      consentToShareWithOwner: !!sh.consentToShareWithOwner,
      pickupUserName: sh.pickupUserName ?? null,
      pickupDateTime: sh.pickUpDateTime ?? null,
      careCondition: it.bcs?.careCtrl.value ?? [],
      bcsNutrition: it.bcs?.nutritionCtrl.value ?? null,
      temperament: it.bcs?.temperamentCtrl.value ?? [],
      finderSignature: this.sharedSignature.finderSignature ?? null,
      employeeSignature: this.sharedSignature.employeeSignature ?? null,
      signatureLocation: this.sharedSignature.signatureLocation ?? null,
      signatureDate: (this.sharedSignature.finderSignature || this.sharedSignature.employeeSignature)
        ? (this.sharedSignature.signatureDate ?? new Date())
        : null,
      animalShelter: this.animal?.animalShelter ?? { id: sh.shelterId },
      shelterChanged: false,
    };
    if (sh.competentAuthorityId) {
      payload.competentAuthority = { id: sh.competentAuthorityId };
    }
    return payload;
  }

  onSubmitAndSend(): void {
    if (this.foundProcessMode) {
      this.submitFoundProcess(true);
      return;
    }
    this.onSubmit();
  }

  // ---------- Prefill ----------
  private buildPrefilledItem(seedIdentity = false): DuplicateItem {
    const src = this.animal as any;
    const a: AnimalDraft = {
      name: seedIdentity ? (src?.name ?? null) : null,
      // Früheren Namen nur fürs Original übernehmen, nicht für Duplikate
      nameOld: seedIdentity ? (this.animal?.nameOld ?? null) : null,
      category: this.animal?.category ?? null,
      type: this.animal?.type ?? null,
      breed: this.animal?.breed ?? null,
      sex: (this.animal?.sex as any) ?? null,

      coatType: null,
      coatColor: this.animal?.coatColor ?? null,
      badge: this.animal?.badge ?? null,
      specialNotice: this.animal?.specialNotice ?? null,

      birthApprox: this.animal?.birthApprox ?? null,
      birthdayType: this.animal?.birthday
        ? false
        : this.animal?.birthYear
          ? true
          : null,
      birthday: this.asIsoDate((this.animal as any)?.birthday) ?? null,
      birthYear: (this.animal as any)?.birthYear ?? null,
      birthPlace: this.animal?.birthPlace ?? null,

      weight: this.animal?.weight ?? null,

      // Identität (Name + Nummern) nur für Item #0 im Fundtierprozess übernehmen,
      // ansonsten bewusst leer lassen
      oldBookNumberList: seedIdentity && src?.oldBookNumberList?.length ? [...src.oldBookNumberList] : [null],
      oldBookNumber: null,
      transponderNumber: seedIdentity && src?.transponderNumber?.length ? [...src.transponderNumber] : [null],
      lifeNumber: seedIdentity ? (src?.lifeNumber ?? null) : null,
      petPassportNumber: seedIdentity ? (src?.petPassportNumber ?? null) : null,
      equinePassportNumber: seedIdentity ? (src?.equinePassportNumber ?? null) : null,
      ringNumber: seedIdentity ? (src?.ringNumber ?? null) : null,
      earTag: seedIdentity ? (src?.earTag ?? null) : null,
      tattoo: seedIdentity ? (src?.tattoo ?? null) : null,
      tattooLocation: seedIdentity ? (src?.tattooLocation ?? null) : null,

      // Mutter vom Original erben (Id immer, Anzeige sobald nachgeladen)
      motherId: this.animal?.motherId ?? null,
      mother: this.inheritedMother,
    };

    if (this.animal?.coatType?.length ) {
        a.coatType = [...this.animal.coatType];
      }

    const o: OriginDraft = this.origin
      ? {
        dateOfReceipt: this.asIsoDate(this.origin.dateOfReceipt) ?? null,
        location: this.origin.location ?? null,
        specialInformation: this.origin.specialInformation ?? null,
        otherComments: this.origin.otherComments ?? null,
      }
      : {
        dateOfReceipt: null,
        location: null,
        specialInformation: null,
        otherComments: null,
      };

    const item: DuplicateItem = { animal: a, origin: o, bcs: this.buildBcsControls(seedIdentity) };

    return item;
  }

  private buildBcsControls(seedFromOrigin = false): BcsControls {
    const validators = this.foundProcessMode ? [Validators.required] : [];
    const care = seedFromOrigin ? (((this.origin as any)?.careCondition as CareCondition[]) ?? []) : [];
    const nutrition = seedFromOrigin ? (((this.origin as any)?.bcsNutrition as NutritionCondition) ?? null) : null;
    const temperament = seedFromOrigin ? (((this.origin as any)?.temperament as TemperamentCondition[]) ?? []) : [];
    return {
      careCtrl: new FormControl<CareCondition[] | null>(care, { validators }),
      nutritionCtrl: new FormControl<NutritionCondition | null>(nutrition, { validators }),
      temperamentCtrl: new FormControl<TemperamentCondition[] | null>(temperament, { validators }),
    };
  }

  private asIsoDate(d?: string | Date | null): string | null {
    if (!d) return null;
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return null;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  // ---------- Payload Builder ----------

  private buildAnimalPayload(draft: AnimalDraft): Partial<AnimalModel> {
    // Ausgangsbasis: Parent-Tier
    const base: any = { ...this.animal };

    // Alles entfernen, was NICHT übernommen / gesendet werden soll
    this.excludedAnimalFields.forEach((key) => {
      delete base[key as keyof AnimalModel];
    });

    // Jetzt die Felder aus dem Draft überschreiben/setzen
    base.name = draft.name?.trim() || '';
    base.nameOld = draft.nameOld || null;
    base.category = draft.category;
    base.type = draft.type;
    base.breed = draft.breed;
    base.sex = draft.sex;

    base.birthApprox = draft.birthApprox;
    base.birthPlace = draft.birthPlace;
    base.birthday = draft.birthday;
    base.birthYear = draft.birthYear;
    base.birthdayType = draft.birthdayType;

    base.weight = draft.weight;

    base.oldBookNumberList = draft.oldBookNumberList.filter((v) => !!v) as string[];
    base.transponderNumber = draft.transponderNumber.filter((v) => !!v) as string[];
    base.lifeNumber = draft.lifeNumber;
    base.petPassportNumber = draft.petPassportNumber;
    base.equinePassportNumber = draft.equinePassportNumber;
    base.ringNumber = draft.ringNumber;
    base.earTag = draft.earTag;
    base.tattoo = draft.tattoo;
    base.tattooLocation = draft.tattooLocation;
    base.coatColor = draft.coatColor;

    // Website-Meta (Title-Tag / Meta-Description) nicht vom Original übernehmen
    base.websiteMetaTitle = null;
    base.websiteMetaDescription = null;

    // Mutter als { id } senden (wie edit-/add-dialog); rohe motherId nicht mitschicken
    delete base.motherId;
    if (draft.motherId) {
      base.mother = { id: draft.motherId };
    } else {
      delete base.mother;
    }

    return base as Partial<AnimalModel>;
  }

  private buildOriginPayload(draft: OriginDraft): Partial<AnimalOriginModel> {
    if (!this.origin) {
      return {
        dateOfReceipt: draft.dateOfReceipt,
        location: draft.location,
        specialInformation: draft.specialInformation,
        otherComments: draft.otherComments,
        givingContact: undefined,
        owningContact: undefined,
        animalShelter: this.animal?.animalShelter ?? null,
        shelterChanged: false,
      } as Partial<AnimalOriginModel>;
    }

    const base: any = { ...this.origin };

    // Sicherstellen, dass keine Id/Animal aus dem Parent im Payload landen
    delete base.id;
    delete base.animal;
    this.excludedOriginFields.forEach((field) => {
      delete base[field as keyof AnimalOriginModel];
    });

    return {
      ...base,
      dateOfReceipt: draft.dateOfReceipt,
      location: draft.location,
      specialInformation: draft.specialInformation,
      otherComments: draft.otherComments,
      // ToDo
      // givingContact: this.origin.givingContact
      //   ? { id: this.origin.givingContact.id }
      //   : undefined,

      // owningContact: this.origin.owningContact
      //   ? { id: this.origin.owningContact.id }
      //   : undefined,

      animalShelter: this.animal?.animalShelter ?? null,
      shelterChanged: false,
    } as Partial<AnimalOriginModel>;
  }

  // ---------- Arrays ----------
  addTransponderNumber(i: number) {
    this.items[i].animal.transponderNumber.push(null);
  }
  removeTransponderNumber(i: number, j: number) {
    const arr = this.items[i].animal.transponderNumber;
    if (arr.length > 1) arr.splice(j, 1);
  }
  addOldBookNumber(i: number) {
    this.items[i].animal.oldBookNumberList.push(null);
  }
  removeOldBookNumber(i: number, j: number) {
    this.items[i].animal.oldBookNumberList.splice(j, 1);
  }

  // ---------- Gewicht ----------
  onWeightInput(i: number, ev: Event) {
    const input = ev.target as HTMLInputElement;
    const val = (input.value ?? '').trim();
    if (!val) {
      this.items[i].animal.weight = null;
      this.weightInputInvalid[i] = false;
      return;
    }
    if (!/^\d{1,3}(,\d{1,3})?$/.test(val)) {
      this.weightInputInvalid[i] = true;
      return;
    }
    const kg = parseFloat(val.replace(',', '.'));
    this.items[i].animal.weight = Math.round(kg * 1000); // Gramm
    this.weightInputInvalid[i] = false;
  }

  // ---------- DatePicker Events ----------
  onBirthdayChanged(i: number, iso: string) {
    this.items[i].animal.birthday = iso || null;
  }
  onBirthdayApproxChanged(i: number, approx: boolean) {
    this.items[i].animal.birthApprox = approx;
  }
  onBirthdayYearOnlyChanged(i: number, yearOnly: boolean) {
    this.items[i].animal.birthdayType = yearOnly;
  }

  onOriginDateChanged(i: number, iso: string) {
    this.items[i].origin.dateOfReceipt = iso || null;
  }

  trackByIndex(index: number, _item: unknown): number {
    return index;
  }
}